/**
 * /payroll-l3/gl-setup — GL setup diagnostic for the union module.
 *
 * Calls GET /api/payroll-l3/gl-setup-status and renders three checks:
 *   1. Active bucket rule set is at v3+
 *   2. Active ledger template is at v3+
 *   3. Required GL accounts (5240-5245, 2249, 2250-2255, 2259, 2260, 2110)
 *      are present in the entity's chart of accounts
 *
 * Each row turns green (CheckCircle) when satisfied, red (Error) when
 * not. Missing accounts list shows the codes the admin needs to add.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import EditIcon from "@mui/icons-material/Edit";
import RestoreIcon from "@mui/icons-material/Restore";

import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";
import { codeAndName } from "../lib/displayCode";

export const Route = createFileRoute("/_auth/payroll-l3/gl-setup")({
  component: GlSetupPage,
});

interface GLSetupStatus {
  ok: boolean;
  bucket_rule_set: {
    active: boolean;
    name: string | null;
    version: number | null;
    version_ok: boolean;
    min_required_version: number;
  };
  ledger_template: {
    active: boolean;
    name: string | null;
    version: number | null;
    version_ok: boolean;
    min_required_version: number;
  };
  accounts: {
    all_present: boolean;
    missing_count: number;
    rows: { code: string; label: string; present: boolean }[];
  };
}

function StatusIcon({ ok }: { ok: boolean }) {
  return ok ? (
    <CheckCircleIcon color="success" fontSize="small" />
  ) : (
    <ErrorIcon color="error" fontSize="small" />
  );
}

interface ActiveRuleRow {
  fringe_type_code: string;
  label: string;
  source: "override" | "seed_default";
  override_id: string | null;
  debit_account_id: string | null;
  debit_account_code: string | null;
  debit_account_name: string | null;
  credit_account_id: string | null;
  credit_account_code: string | null;
  credit_account_name: string | null;
  notes: string | null;
  default_debit_account_code: string;
  default_credit_account_code: string;
}

interface AccountOption {
  id: string;
  code: string;
  name: string;
  account_type: string;
}

interface ProvisionResult {
  created: { code: string; name: string }[];
  already_present: string[];
  ok: boolean;
}

function GlSetupPage() {
  const [editing, setEditing] = useState<ActiveRuleRow | null>(null);
  const [provisionResult, setProvisionResult] = useState<ProvisionResult | null>(null);
  const qc = useQueryClient();

  const statusQ = useQuery({
    queryKey: ["payroll-l3", "gl-setup-status"],
    queryFn: () => api<GLSetupStatus>("/api/payroll-l3/gl-setup-status"),
  });

  const rulesQ = useQuery({
    queryKey: ["payroll-l3", "active-union-rules"],
    queryFn: () =>
      api<{ rows: ActiveRuleRow[] }>("/api/payroll-l3/gl-setup-status/active-union-rules"),
  });

  const accountsQ = useQuery({
    queryKey: ["accounts", "for-override-editor"],
    queryFn: () => api<AccountOption[]>("/api/accounts?include_inactive=false"),
    staleTime: 60_000,
  });

  const deleteMut = useMutation({
    mutationFn: (override_id: string) =>
      api(`/api/payroll-l3/gl-account-overrides/${override_id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l3", "active-union-rules"] });
      qc.invalidateQueries({ queryKey: ["payroll-l3", "gl-setup-status"] });
    },
  });

  const provisionMut = useMutation({
    mutationFn: () =>
      api<ProvisionResult>("/api/payroll-l3/gl-setup-status/provision-accounts", {
        method: "POST",
      }),
    onSuccess: (result) => {
      setProvisionResult(result);
      qc.invalidateQueries({ queryKey: ["payroll-l3", "gl-setup-status"] });
      qc.invalidateQueries({ queryKey: ["accounts"] });
    },
  });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>
          L3 GL Setup
        </Typography>
        <HelpHint docPath="payroll-l3/gl-setup.md" label="GL setup help" />
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Confirms the union pay-run engine can post to the General Ledger
        cleanly. Three checks: the active bucket rule set is at v3+,
        the active ledger template is at v3+, and your chart of accounts
        has the union-fringe and dues accounts the v3 template posts to.
      </Typography>

      {statusQ.isLoading && <CircularProgress size={24} />}
      {statusQ.error && (
        <Alert severity="error">Failed to load setup status.</Alert>
      )}

      {statusQ.data && (
        <Stack spacing={2}>
          {statusQ.data.ok ? (
            <Alert severity="success">
              GL setup is complete. L3 envelopes will post to journal lines.
            </Alert>
          ) : (
            <Alert severity="warning">
              GL setup is incomplete — some checks failed. Review the
              rows below and fix the gaps before running a union
              pay run.
            </Alert>
          )}

          {/* Rule set + template */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Money-core configuration
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Component</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Active bucket rule set</TableCell>
                    <TableCell>
                      {statusQ.data.bucket_rule_set.name ?? "—"}
                    </TableCell>
                    <TableCell>
                      v{statusQ.data.bucket_rule_set.version ?? "?"} (need ≥
                      v{statusQ.data.bucket_rule_set.min_required_version})
                    </TableCell>
                    <TableCell>
                      <StatusIcon ok={statusQ.data.bucket_rule_set.version_ok} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Active ledger template</TableCell>
                    <TableCell>
                      {statusQ.data.ledger_template.name ?? "—"}
                    </TableCell>
                    <TableCell>
                      v{statusQ.data.ledger_template.version ?? "?"} (need ≥
                      v{statusQ.data.ledger_template.min_required_version})
                    </TableCell>
                    <TableCell>
                      <StatusIcon ok={statusQ.data.ledger_template.version_ok} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              {(!statusQ.data.bucket_rule_set.version_ok ||
                !statusQ.data.ledger_template.version_ok) && (
                <Typography variant="caption" color="text.secondary" mt={1} display="block">
                  Run the implementation-agent bootstrap to install the
                  current seed: <code>python -m app.cli bootstrap</code>.
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Required accounts */}
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Required GL accounts
                </Typography>
                <Stack direction="row" gap={1} alignItems="center">
                  {statusQ.data.accounts.missing_count > 0 && (
                    <Chip
                      label={`${statusQ.data.accounts.missing_count} missing`}
                      color="error"
                      size="small"
                    />
                  )}
                  {statusQ.data.accounts.missing_count > 0 && (
                    <Tooltip title="Automatically create all missing union GL accounts with standard codes">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={
                          provisionMut.isPending
                            ? <CircularProgress size={14} color="inherit" />
                            : <AutoFixHighIcon />
                        }
                        disabled={provisionMut.isPending}
                        onClick={() => provisionMut.mutate()}
                      >
                        Auto-create missing
                      </Button>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>

              {provisionMut.isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Failed to create accounts:{" "}
                  {provisionMut.error instanceof Error
                    ? provisionMut.error.message
                    : "Unknown error"}
                </Alert>
              )}

              {provisionResult && provisionResult.created.length > 0 && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setProvisionResult(null)}>
                  Created {provisionResult.created.length} account
                  {provisionResult.created.length !== 1 ? "s" : ""}:{" "}
                  {provisionResult.created.map((a) => a.code).join(", ")}.
                  {provisionResult.already_present.length > 0 && (
                    <> {provisionResult.already_present.length} were already present.</>
                  )}
                </Alert>
              )}

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Purpose</TableCell>
                    <TableCell>In chart</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statusQ.data.accounts.rows.map((a) => (
                    <TableRow key={a.code}>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {a.code}
                      </TableCell>
                      <TableCell>{a.label}</TableCell>
                      <TableCell>
                        <StatusIcon ok={a.present} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {statusQ.data.accounts.missing_count > 0 && !provisionResult && (
                <Typography variant="caption" color="text.secondary" mt={1} display="block">
                  Click <strong>Auto-create missing</strong> to let the system add
                  the standard union accounts automatically, or add them manually via
                  Setup → Chart of Accounts.
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Active union posting rules — read-only review + per-row override */}
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Active union posting rules
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    What each fringe type posts to today. Click <em>Override</em>
                    {" "}on any row to route it to your own COA codes — no JSON edits required.
                  </Typography>
                </Box>
              </Stack>

              {rulesQ.isLoading && <CircularProgress size={24} />}
              {rulesQ.data && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fringe type</TableCell>
                      <TableCell>Debit (expense)</TableCell>
                      <TableCell>Credit (payable)</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rulesQ.data.rows.map((r) => (
                      <TableRow key={r.fringe_type_code} hover>
                        <TableCell>
                          <Stack>
                            <span style={{ fontWeight: 500 }}>{r.label}</span>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontFamily: "monospace" }}
                            >
                              {r.fringe_type_code}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontFamily: "monospace" }}>
                          {r.debit_account_code ?? "—"}
                          {r.debit_account_name && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              {r.debit_account_name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "monospace" }}>
                          {r.credit_account_code ?? "—"}
                          {r.credit_account_name && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              {r.credit_account_name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {r.source === "override" ? (
                            <Tooltip
                              title={
                                r.notes ||
                                `Override of seed default ${r.default_debit_account_code}/${r.default_credit_account_code}`
                              }
                            >
                              <Chip
                                label="Override"
                                size="small"
                                color="primary"
                              />
                            </Tooltip>
                          ) : (
                            <Chip
                              label="Default"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => setEditing(r)}
                          >
                            {r.source === "override" ? "Edit" : "Override"}
                          </Button>
                          {r.source === "override" && r.override_id && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (
                                  confirm(
                                    `Restore the seed default for ${r.label}? The default uses ${r.default_debit_account_code}/${r.default_credit_account_code}.`
                                  )
                                ) {
                                  deleteMut.mutate(r.override_id!);
                                }
                              }}
                              disabled={deleteMut.isPending}
                              title="Restore default"
                            >
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Stack>
      )}

      {editing && (
        <OverrideEditorDialog
          row={editing}
          accounts={accountsQ.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </Box>
  );
}

// ── Override editor ─────────────────────────────────────────────────────


function OverrideEditorDialog({
  row,
  accounts,
  onClose,
}: {
  row: ActiveRuleRow;
  accounts: AccountOption[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [debitId, setDebitId] = useState<string | null>(row.debit_account_id);
  const [creditId, setCreditId] = useState<string | null>(row.credit_account_id);
  const [notes, setNotes] = useState<string>(row.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!debitId || !creditId) {
        throw new Error("Pick both a debit and credit account.");
      }
      // PUT replaces the full set; pull the current full list, swap in
      // the edited row, and PUT it all back.
      const current = await api<
        Array<{
          fringe_type_code: string;
          debit_account_id: string;
          credit_account_id: string;
          notes: string | null;
        }>
      >("/api/payroll-l3/gl-account-overrides");
      const filtered = current.filter(
        (r) => r.fringe_type_code !== row.fringe_type_code,
      );
      const updated = [
        ...filtered,
        {
          fringe_type_code: row.fringe_type_code,
          debit_account_id: debitId,
          credit_account_id: creditId,
          notes: notes || null,
        },
      ];
      return api("/api/payroll-l3/gl-account-overrides", {
        method: "PUT",
        body: JSON.stringify(updated),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l3", "active-union-rules"] });
      qc.invalidateQueries({ queryKey: ["payroll-l3", "gl-setup-status"] });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const accountById = new Map(accounts.map((a) => [a.id, a]));

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Override GL routing — {row.label}{" "}
        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontFamily: "monospace" }}>
          {row.fringe_type_code}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Alert severity="info">
            Default routes <strong>{row.default_debit_account_code}</strong> →{" "}
            <strong>{row.default_credit_account_code}</strong>. Pick custom
            accounts to override per-entity. Restore default by deleting the
            override row in the table.
          </Alert>

          <Autocomplete
            options={accounts.filter((a) => a.account_type === "expense" || a.account_type === "liability")}
            getOptionLabel={(a) => codeAndName(a, " — ")}
            value={debitId ? accountById.get(debitId) ?? null : null}
            onChange={(_, v) => setDebitId(v?.id ?? null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  row.fringe_type_code === "DUES"
                    ? "Debit account (typically Net Pay Payable)"
                    : "Debit account (Expense)"
                }
              />
            )}
          />

          <Autocomplete
            options={accounts.filter((a) => a.account_type === "liability")}
            getOptionLabel={(a) => codeAndName(a, " — ")}
            value={creditId ? accountById.get(creditId) ?? null : null}
            onChange={(_, v) => setCreditId(v?.id ?? null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Credit account (Liability / Payable)"
              />
            )}
          />

          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            placeholder="e.g. 'Painters Trust H&W goes to 5240-PAINT in our COA'"
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            setError(null);
            saveMut.mutate();
          }}
          disabled={saveMut.isPending || !debitId || !creditId}
        >
          {saveMut.isPending ? "Saving…" : "Save override"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
